import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import ColorPicker from './ColorPicker';
import {
  CUSTOMISABLE_VARS,
  buildCustomThemeVars,
  checkContrastPairs,
  adjustForContrast,
  deriveActionFg,
} from '../utils/color';

/**
 * ThemeEditor modal.
 *
 * Props:
 *   theme: { id, name, vars }  — theme being edited; pass null to create new
 *   onSave: (theme) => void    — called with the saved theme object
 *   onClose: () => void
 */
export default function ThemeEditor({ theme, onSave, onClose }) {
  const { t } = useTranslation();

  const [name, setName] = useState(theme?.name ?? t('customTheme.newThemeName'));
  // Merge DEFAULT_VARS first so existing themes get sensible defaults for newly added vars.
  // If the saved theme has no --c-action-fg (older format), derive it from its action-bg
  // rather than blindly inheriting DEFAULT_VARS' #ffffff (which would break light-bg themes).
  const [vars, setVars] = useState(() => {
    // When creating a new theme, seed from the currently active theme's computed CSS vars
    // so the editor starts with colours that match what the user sees on screen.
    const seed = theme?.vars ?? getCurrentThemeVars();
    const merged = { ...DEFAULT_VARS, ...seed };
    if (theme && !theme.vars?.['--c-action-fg']) {
      merged['--c-action-fg'] = deriveActionFg(merged['--c-action-bg']);
    }
    return merged;
  });
  // null = colour grid visible; a CSS key string = picker open for that var
  const [selectedKey, setSelectedKey] = useState(null);

  const allVars = buildCustomThemeVars(vars);
  const pairs   = checkContrastPairs(vars);

  function handleVarChange(key, hex) {
    setVars((prev) => {
      const next = { ...prev, [key]: hex };
      // When the button background changes, auto-update the button text colour
      // so it stays readable — the user can still override it manually afterwards.
      if (key === '--c-action-bg') {
        next['--c-action-fg'] = deriveActionFg(hex);
      }
      return next;
    });
  }

  function handleSave() {
    onSave({
      id:        theme?.id ?? crypto.randomUUID(),
      name:      name.trim() || t('customTheme.newThemeName'),
      vars,
      createdAt: theme?.createdAt ?? Date.now(),
    });
  }

  // Build an inline-style object for the live preview container
  const previewStyle = Object.fromEntries(
    Object.entries(allVars).map(([k, v]) => [k, v])
  );

  const selectedVarMeta = CUSTOMISABLE_VARS.find((v) => v.key === selectedKey);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('customTheme.editorTitle')}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel — EDITOR_BASE_VARS pins the standard dark theme so the editor UI is always
           readable regardless of which custom theme is currently active. The live preview
           section overrides these vars locally to show the theme being edited. */}
      <div
        className="relative z-10 w-full sm:max-w-3xl max-h-[95dvh] flex flex-col t-bg-surface t-border border rounded-t-2xl sm:rounded-2xl overflow-hidden"
        style={EDITOR_BASE_VARS}
      >
        {/* Header — title + inline theme name input + close */}
        <div className="flex items-center gap-3 px-5 py-3 border-b t-border shrink-0">
          <h2 className="font-semibold t-text text-base whitespace-nowrap shrink-0">
            {t('customTheme.editorTitle')}
          </h2>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={32}
            aria-label={t('customTheme.themeName')}
            className="flex-1 t-bg-input t-border-input border rounded-lg px-3 py-1.5 t-text text-sm t-focus-input"
          />
          <button
            onClick={onClose}
            className="t-text-muted hover:t-text transition-colors t-focus-ring rounded p-1 shrink-0"
            aria-label={t('customTheme.close')}
          >
            ✕
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="overflow-y-auto flex-1 p-4 space-y-4">

          {/* Main two-panel row */}
          <div className="flex flex-col sm:flex-row gap-4">

            {/* Left panel: colour selector — toggles between two-column grid and picker */}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium t-text-muted uppercase tracking-wider mb-2">
                {selectedKey
                  ? (selectedVarMeta ? t(selectedVarMeta.labelKey) : t('customTheme.selectColour'))
                  : t('customTheme.selectColour')
                }
              </p>

              {selectedKey === null ? (
                /* ── Two-column button grid ── */
                <div className="flex gap-1.5">
                  {/* Column 1: backgrounds + main text colours (indices 0–6) */}
                  <div className="flex-1 min-w-0 space-y-1.5">
                    {CUSTOMISABLE_VARS.slice(0, 7).map((meta) => (
                      <ColourButton
                        key={meta.key}
                        meta={meta}
                        hex={vars[meta.key] || '#888888'}
                        pairs={pairs}
                        t={t}
                        onClick={() => setSelectedKey(meta.key)}
                      />
                    ))}
                  </div>
                  {/* Column 2: disabled + accent + status (indices 7–13) */}
                  <div className="flex-1 min-w-0 space-y-1.5">
                    {CUSTOMISABLE_VARS.slice(7).map((meta) => (
                      <ColourButton
                        key={meta.key}
                        meta={meta}
                        hex={vars[meta.key] || '#888888'}
                        pairs={pairs}
                        t={t}
                        onClick={() => setSelectedKey(meta.key)}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                /* ── Colour picker (replaces grid, no scroll needed) ── */
                <div className="t-bg-input t-border border rounded-xl p-3 space-y-3">
                  {/* Picker header: swatch + var name + back arrow */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setSelectedKey(null)}
                      className="t-text-muted hover:t-text transition-colors t-focus-ring rounded p-0.5 shrink-0"
                      aria-label={t('customTheme.closePicker')}
                    >
                      ←
                    </button>
                    <span
                      className="w-4 h-4 rounded-sm border border-white/20 shrink-0"
                      style={{ backgroundColor: vars[selectedKey] || '#888' }}
                      aria-hidden="true"
                    />
                    <span className="text-xs font-medium t-text flex-1 truncate">
                      {selectedVarMeta ? t(selectedVarMeta.labelKey) : ''}
                    </span>
                    <button
                      onClick={() => setSelectedKey(null)}
                      className="t-text-muted hover:t-text transition-colors t-focus-ring rounded p-0.5 shrink-0"
                      aria-label={t('customTheme.closePicker')}
                    >
                      ✕
                    </button>
                  </div>
                  <ColorPicker
                    value={vars[selectedKey] || '#888888'}
                    onChange={(hex) => handleVarChange(selectedKey, hex)}
                  />
                </div>
              )}
            </div>

            {/* Right panel: live preview — always fully visible */}
            <div className="sm:w-56 shrink-0">
              <p className="text-xs font-medium t-text-muted uppercase tracking-wider mb-2">
                {t('customTheme.preview')}
              </p>
              <LivePreview style={previewStyle} t={t} />
            </div>
          </div>

          {/* Contrast — always shown, three-tier labelling */}
          <div className="t-bg-input rounded-xl p-3 space-y-2">
            <p className="text-xs font-semibold t-text-muted uppercase tracking-wider">
              {t('customTheme.contrastTitle')}
            </p>
            <ul className="space-y-1">
              {pairs.map((pair) => (
                <ContrastRow
                  key={pair.fgKey + pair.bgKey}
                  pair={pair}
                  t={t}
                  onAutoCorrect={(key, hex) => handleVarChange(key, hex)}
                />
              ))}
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-5 py-4 border-t t-border shrink-0">
          <button
            onClick={onClose}
            className="flex-1 t-bg-input t-text font-medium rounded-lg px-4 py-2.5 text-sm t-focus-ring hover:t-bg-hover transition-colors"
          >
            {t('customTheme.cancel')}
          </button>
          <button
            onClick={handleSave}
            className="flex-1 t-btn-primary font-semibold rounded-lg px-4 py-2.5 text-sm t-focus-ring"
          >
            {t('customTheme.save')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/** A single colour variable button in the selector grid. */
function ColourButton({ meta, hex, pairs, t, onClick }) {
  const { key, labelKey } = meta;
  const affectedPairs = pairs.filter((p) => p.fgKey === key || p.bgKey === key);
  const worstLevel = affectedPairs.reduce((worst, p) => {
    if (p.level === 'unreadable') return 'unreadable';
    if (p.level === 'fail' && worst !== 'unreadable') return 'fail';
    if (p.level === 'AA-large' && worst !== 'fail' && worst !== 'unreadable') return 'AA-large';
    return worst;
  }, null);

  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left transition-colors t-focus-ring text-xs t-bg-input t-text hover:t-bg-hover"
    >
      <span
        className="w-4 h-4 rounded-sm border border-white/20 shrink-0"
        style={{ backgroundColor: hex }}
        aria-hidden="true"
      />
      <span className="flex-1 t-text-secondary truncate">{t(labelKey)}</span>
      {(worstLevel === 'unreadable' || worstLevel === 'fail') && (
        <span className="text-xs t-text-error shrink-0" aria-label={t('customTheme.contrastFail')}>⚠</span>
      )}
      {worstLevel === 'AA-large' && (
        <span className="text-xs t-text-warning opacity-70 shrink-0" aria-label={t('customTheme.contrastLow')}>⚠</span>
      )}
    </button>
  );
}

/** One row in the always-visible contrast table. */
function ContrastRow({ pair, t, onAutoCorrect }) {
  const ratio = pair.ratio != null ? pair.ratio.toFixed(2) : '—';
  const fgLabel = t(CUSTOMISABLE_VARS.find((v) => v.key === pair.fgKey)?.labelKey ?? pair.fgKey);
  const bgLabel = t(CUSTOMISABLE_VARS.find((v) => v.key === pair.bgKey)?.labelKey ?? pair.bgKey);

  let levelLabel, levelClass;
  switch (pair.level) {
    case 'AAA':
    case 'AA':
      levelLabel = t('customTheme.contrastGood');
      levelClass = 't-text-success';
      break;
    case 'AA-large':
    case 'fail':
      levelLabel = t('customTheme.contrastPoor');
      levelClass = 't-text-warning';
      break;
    case 'unreadable':
      levelLabel = t('customTheme.contrastUnreadable');
      levelClass = 't-text-error';
      break;
    default:
      levelLabel = '—';
      levelClass = 't-text-muted';
  }

  // Derived pairs (action-fg/action-bg) are auto-computed and can't be directly edited.
  // Non-good, non-derived pairs get a Fix button that adjusts the fg colour.
  const canFix = !pair.derived &&
    (pair.level === 'unreadable' || pair.level === 'fail' || pair.level === 'AA-large');

  // Target: 'unreadable' → raise to just above 2:1 (low end of Poor)
  //         'fail'/'AA-large' → raise to just above 4.5:1 (low end of Good)
  const targetRatio = pair.level === 'unreadable' ? 2.05 : 4.55;

  function handleFix() {
    const fixed = adjustForContrast(pair.fg, pair.bg, targetRatio);
    onAutoCorrect(pair.fgKey, fixed);
  }

  return (
    <li className="flex items-center gap-1.5 text-xs">
      <span
        className="w-3 h-3 rounded-sm border border-white/20 shrink-0"
        style={{ backgroundColor: pair.fg }}
        aria-hidden="true"
      />
      <span
        className="w-3 h-3 rounded-sm border border-white/20 shrink-0"
        style={{ backgroundColor: pair.bg }}
        aria-hidden="true"
      />
      <span className="flex-1 t-text-muted truncate">{fgLabel} {t('customTheme.on')} {bgLabel}</span>
      <span className={`font-medium shrink-0 ${levelClass}`}>{ratio}:1</span>
      <span className={`font-medium shrink-0 ${levelClass} w-20 text-right`}>{levelLabel}</span>
      {canFix ? (
        <button
          onClick={handleFix}
          className="shrink-0 text-xs px-1.5 py-0.5 rounded t-bg-surface t-text-accent hover:t-text transition-colors t-focus-ring"
          aria-label={`${t('customTheme.autoCorrect')}: ${fgLabel}`}
        >
          {t('customTheme.autoCorrect')}
        </button>
      ) : (
        /* Spacer to keep column alignment when no button */
        <span className="shrink-0 w-11.5" aria-hidden="true" />
      )}
    </li>
  );
}

function LivePreview({ style, t }) {
  return (
    /* Apply theme vars inline so they cascade within the preview only,
       overriding the EDITOR_BASE_VARS set on the panel. */
    <div
      style={style}
      className="rounded-xl border t-border overflow-hidden"
    >
      {/* Simulated card on surface — each element labelled with its own var name */}
      <div className="t-bg-surface p-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold t-text">{t('customTheme.vars.text')}</span>
          <span className="text-xs t-text-accent t-bg-accent-muted px-2 py-0.5 rounded-full shrink-0">
            {t('customTheme.vars.textAccent')}
          </span>
        </div>
        <p className="text-xs t-text-secondary">{t('customTheme.vars.textSecondary')}</p>
        <p className="text-xs t-text-muted">{t('customTheme.vars.textMuted')}</p>
        <p className="text-xs t-text-disabled">{t('customTheme.vars.textDisabled')}</p>
        {/* Input */}
        <div className="t-bg-input t-border-input border rounded-lg px-3 py-1.5">
          <span className="text-xs t-text-muted">{t('customTheme.vars.bgInput')}</span>
        </div>
        {/* Action button */}
        <button className="w-full t-btn-primary text-xs font-semibold rounded-lg px-3 py-1.5 pointer-events-none">
          {t('customTheme.vars.actionBg')}
        </button>
        {/* Status colours */}
        <div className="flex gap-2 flex-wrap">
          <span className="text-xs t-text-success font-medium">{t('customTheme.vars.success')}</span>
          <span className="text-xs t-text-error font-medium">{t('customTheme.vars.error')}</span>
          <span className="text-xs t-text-warning font-medium">{t('customTheme.vars.warning')}</span>
          <span className="text-xs t-text-highlight font-medium">{t('customTheme.vars.highlight')}</span>
        </div>
      </div>
      {/* Page bg strip */}
      <div className="t-bg-page px-3 py-1.5 flex items-center gap-3 border-t t-border">
        <span className="text-xs t-text-muted">{t('customTheme.vars.bgPage')}</span>
        <span className="text-xs t-text-accent">{t('customTheme.vars.textAccent')}</span>
      </div>
    </div>
  );
}

/** Read all CUSTOMISABLE_VARS from the live document so a new theme starts with current colours. */
function getCurrentThemeVars() {
  const style = getComputedStyle(document.documentElement);
  const result = {};
  for (const { key } of CUSTOMISABLE_VARS) {
    result[key] = style.getPropertyValue(key).trim();
  }
  return result;
}

/** Fallback vars used when getComputedStyle returns empty strings (e.g. SSR / test env).
 *  Matches the standard (dark) theme in index.css :root. */
const DEFAULT_VARS = {
  '--c-bg-page':        '#111827',
  '--c-bg-surface':     '#1f2937',
  '--c-bg-input':       '#374151',
  '--c-border':         '#374151',
  '--c-text':           '#f9fafb',
  '--c-text-secondary': '#e5e7eb',
  '--c-text-muted':     '#9ca3af',
  '--c-text-disabled':  '#6b7280',
  '--c-text-accent':    '#a78bfa',
  '--c-action-bg':      '#7c3aed',
  '--c-action-fg':      '#ffffff',
  '--c-success':        '#4ade80',
  '--c-error':          '#f87171',
  '--c-warning':        '#fbbf24',
  '--c-highlight':      '#facc15',
};

/**
 * CSS vars pinned to the standard dark theme (index.css :root).
 * Applied as inline style on the ThemeEditor panel so the editor UI is always
 * readable regardless of which custom theme the user currently has active.
 * The live preview overrides these locally with the theme being edited.
 */
const EDITOR_BASE_VARS = {
  '--c-bg-page':        '#030712',
  '--c-bg-surface':     '#111827',
  '--c-bg-input':       '#1f2937',
  '--c-border':         '#374151',
  '--c-text':           '#f9fafb',
  '--c-text-secondary': '#d1d5db',
  '--c-text-muted':     '#9ca3af',
  '--c-text-disabled':  '#6b7280',
  '--c-text-accent':    '#3a88ed',
  '--c-action-bg':      '#3a88ed',
  '--c-action-fg':      '#ffffff',
  '--c-focus':          '#3a88ed',
  '--c-success':        '#4ade80',
  '--c-error':          '#f87171',
  '--c-warning':        '#fbbf24',
  '--c-highlight':      '#facc15',
};
